import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Download, X } from "lucide-react";
import { API, logErr, inr, fmtErr } from "../../lib/api";

const STATUSES = ["placed", "confirmed", "shipped", "out_for_delivery", "delivered", "cancelled", "return_requested", "returned", "refunded"];

export default function AdminOrders() {
  const [orders, setOrders] = useState(null);
  const [filter, setFilter] = useState("");
  const [detail, setDetail] = useState(null);

  const load = useCallback(() => API.get("/admin/orders", { params: filter ? { status: filter } : {} })
    .then((r) => setOrders(r.data)).catch((err) => logErr("Failed to load orders:", err)), [filter, API, logErr]);
  useEffect(() => { load(); document.title = "Orders | Admin"; }, [load]);

  const openDetail = async (id) => {
    const r = await API.get(`/admin/orders/${id}`);
    setDetail(r.data);
  };

  const setStatus = async (id, status) => {
    try {
      await API.put(`/admin/orders/${id}/status`, { status });
      toast.success(`Status → ${status.replace(/_/g, " ")}`);
      load();
      if (detail?.id === id) openDetail(id);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  if (!orders) return <div className="text-muted-foreground">Loading...</div>;

  return (
    <div data-testid="admin-orders">
      <h1 className="font-display font-black text-3xl tracking-tighter mb-6">Orders <span className="text-muted-foreground text-lg font-normal">({orders.length})</span></h1>
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        <button onClick={() => setFilter("")} data-testid="filter-status-all"
          className={`px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold border shrink-0 ${!filter ? "border-gold text-gold" : "border-[#262626] text-muted-foreground"}`}>All</button>
        {STATUSES.map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`filter-status-${s}`}
            className={`px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold border shrink-0 transition-colors ${filter === s ? "border-gold text-gold" : "border-[#262626] text-muted-foreground hover:border-gold/50"}`}>
            {s.replace(/_/g, " ")}
          </button>
        ))}
      </div>

      <div className="border border-[#262626] bg-ink-surface overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-muted-foreground border-b border-[#262626]">
              <th className="p-4">Order</th><th className="p-4">Customer</th><th className="p-4">Date</th>
              <th className="p-4">Payment</th><th className="p-4">Total</th><th className="p-4">Status</th><th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.id} className="border-b border-[#262626] hover:bg-ink-elevated/50 cursor-pointer" onClick={() => openDetail(o.id)} data-testid={`order-row-${o.order_number}`}>
                <td className="p-4 font-bold text-xs">{o.order_number}</td>
                <td className="p-4 text-xs">{o.address.name}<br /><span className="text-muted-foreground">{o.email}</span></td>
                <td className="p-4 text-xs text-muted-foreground">{o.created_at.slice(0, 10)}</td>
                <td className="p-4 text-xs">{o.payment.method.toUpperCase()} <span className={`ml-1 ${o.payment.status === "paid" ? "text-green-400" : "text-gold"}`}>{o.payment.status}</span></td>
                <td className="p-4 text-xs font-bold text-gold">{inr(o.total)}</td>
                <td className="p-4">
                  <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 ${o.status === "delivered" ? "bg-green-400/10 text-green-400" : ["cancelled", "refunded"].includes(o.status) ? "bg-red-400/10 text-red-400" : "bg-gold/10 text-gold"}`}>
                    {o.status.replace(/_/g, " ")}
                  </span>
                </td>
                <td className="p-4 text-xs text-gold">View →</td>
              </tr>
            ))}
            {orders.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-xs text-muted-foreground">No orders in this state.</td></tr>}
          </tbody>
        </table>
      </div>

      {detail && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4" data-testid="order-detail-modal">
          <div className="bg-ink-surface border border-[#262626] w-full max-w-2xl p-6 my-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-display font-bold text-xl">{detail.order_number}</h2>
                <p className="text-xs text-muted-foreground">{detail.created_at.slice(0, 16).replace("T", " ")}</p>
              </div>
              <button onClick={() => setDetail(null)} data-testid="close-order-detail"><X size={18} /></button>
            </div>

            <div className="grid sm:grid-cols-2 gap-6 mb-6">
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-gold font-bold mb-2">Customer</p>
                <p className="text-sm font-bold">{detail.address.name}</p>
                <p className="text-xs text-muted-foreground">{detail.email}{detail.address.phone ? ` · ${detail.address.phone}` : ""}</p>
                {detail.customer && <p className="text-[10px] text-gold mt-1">Registered since {detail.customer.created_at.slice(0, 10)}</p>}
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-gold font-bold mb-2">Ship to</p>
                <p className="text-xs text-muted-foreground">{detail.address.line1}{detail.address.line2 ? `, ${detail.address.line2}` : ""}<br />
                  {detail.address.city}, {detail.address.state} — {detail.address.pincode}</p>
              </div>
            </div>

            <div className="space-y-2 mb-6">
              {detail.items.map((i) => (
                <div key={i.sku + i.size} className="flex items-center gap-3 text-xs border border-[#262626] p-3">
                  <img src={i.image} alt="" className="h-12 w-9 object-cover" loading="lazy" />
                  <div className="flex-1">
                    <p className="font-bold">{i.name}</p>
                    <p className="text-muted-foreground">{i.sku} · {i.size}/{i.color} × {i.qty}</p>
                  </div>
                  <p className="font-bold text-gold">{inr(i.price * i.qty)}</p>
                </div>
              ))}
            </div>

            <div className="text-xs space-y-1 mb-6 border-t border-[#262626] pt-4">
              <div className="flex justify-between text-muted-foreground"><span>Subtotal</span><span>{inr(detail.subtotal)}</span></div>
              {detail.discount > 0 && <div className="flex justify-between text-gold"><span>Discount ({detail.coupon_code})</span><span>-{inr(detail.discount)}</span></div>}
              {detail.loyalty_discount > 0 && <div className="flex justify-between text-gold"><span>Stamp card (40%)</span><span>-{inr(detail.loyalty_discount)}</span></div>}
              <div className="flex justify-between text-muted-foreground"><span>Shipping</span><span>{detail.shipping === 0 ? "FREE" : inr(detail.shipping)}</span></div>
              <div className="flex justify-between text-muted-foreground"><span>GST (5% incl.)</span><span>{inr(detail.tax)}</span></div>
              <div className="flex justify-between font-bold text-base pt-2"><span>Total</span><span className="gold-text">{inr(detail.total)}</span></div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <select value={detail.status} onChange={(e) => setStatus(detail.id, e.target.value)} data-testid="order-status-select"
                className="bg-ink border border-[#262626] px-3 py-2.5 text-xs uppercase tracking-wider outline-none focus:border-gold">
                {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
              </select>
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/orders/${detail.id}/invoice`} data-testid="admin-invoice-btn"
                className="inline-flex items-center gap-2 border border-gold text-gold px-4 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-gold/10 transition-colors">
                <Download size={13} /> Invoice PDF
              </a>
            </div>

            <div className="mt-6">
              <p className="text-[10px] uppercase tracking-[0.2em] text-gold font-bold mb-2">Timeline</p>
              <div className="space-y-1.5">
                {detail.timeline.map((t) => (
                  <p key={`${t.status}-${t.at}`} className="text-xs text-muted-foreground">
                    <span className="text-foreground font-bold capitalize">{t.status.replace(/_/g, " ")}</span> — {t.at.slice(0, 16).replace("T", " ")}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
